import React from "react";
import {Button, Select, SelectProps} from "antd";
import type {DefaultOptionType} from "antd/es/select";
import * as styles from "./SelectAllMultiSelect.module.css";

type SelectValue = string;

type Props = Omit<SelectProps<SelectValue[]>, "mode" | "onChange"> & {
    selectAllValues: SelectValue[];
    selectAllLabel?: React.ReactNode;
    deselectAllLabel?: React.ReactNode;
    onChange?: (value: SelectValue[]) => void;
};

type SelectOptionNode = {
    option: DefaultOptionType;
    value?: SelectValue;
    children?: SelectOptionNode[];
};

type SelectOptionElementProps = {
    children?: React.ReactNode;
    value?: string | number | null;
    [name: string]: unknown;
};

type OptionFilterConfig = {
    filterOption: Props["filterOption"];
    filterProperties?: string[];
    groupLabelField: string;
    searchValue: string;
    valueField: string;
};

function SelectAllMultiSelect(props: Props) {
    const {
        selectAllValues,
        selectAllLabel = "Select all",
        deselectAllLabel = "Deselect all",
        allowClear,
        autoClearSearchValue,
        children,
        defaultValue,
        disabled,
        fieldNames,
        filterOption,
        filterSort,
        onChange,
        onOpenChange,
        onSearch,
        optionFilterProp,
        options,
        popupRender,
        searchValue,
        showSearch,
        value,
        ...selectProps
    } = props;
    const [internalValue, setInternalValue] = React.useState<SelectValue[]>(() => normalizeValue(defaultValue));
    const [internalSearchValue, setInternalSearchValue] = React.useState("");
    const searchConfig = {
        autoClearSearchValue,
        filterOption,
        filterSort,
        onSearch,
        optionFilterProp,
        searchValue,
        ...(typeof showSearch === "object" ? showSearch : {})
    };
    const isControlled = "value" in props;
    const selectedValue = isControlled ? normalizeValue(value) : internalValue;
    const selectedCount = selectedValue.length;
    const effectiveSearchValue = searchConfig.searchValue ?? internalSearchValue;
    const hasActiveSearch = effectiveSearchValue.length > 0;
    const childrenAsData = !options && Boolean(children);
    const optionTree = React.useMemo(
        () => options
            ? createOptionTreeFromOptions(options, fieldNames)
            : createOptionTreeFromChildren(children),
        [children, fieldNames, options]
    );
    const visibleSelectAllValues = React.useMemo(
        () => getVisibleSelectAllValues(
            selectAllValues,
            optionTree,
            effectiveSearchValue,
            searchConfig.filterOption,
            searchConfig.optionFilterProp,
            fieldNames,
            childrenAsData
        ),
        [
            childrenAsData,
            effectiveSearchValue,
            fieldNames,
            optionTree,
            searchConfig.filterOption,
            searchConfig.optionFilterProp,
            selectAllValues
        ]
    );
    const effectiveShowSearch = typeof showSearch === "object"
        ? {
            ...searchConfig,
            onSearch: handleSearch,
            searchValue: effectiveSearchValue
        }
        : showSearch;

    return (
        <Select
            {...selectProps}
            mode="multiple"
            allowClear={allowClear ?? true}
            autoClearSearchValue={searchConfig.autoClearSearchValue}
            disabled={disabled}
            fieldNames={fieldNames}
            filterOption={searchConfig.filterOption}
            filterSort={searchConfig.filterSort}
            value={selectedValue}
            onChange={handleSelectChange}
            onOpenChange={handleOpenChange}
            onSearch={handleSearch}
            optionFilterProp={searchConfig.optionFilterProp}
            options={options}
            popupRender={menu => renderPopup(popupRender ? popupRender(menu) : menu)}
            searchValue={effectiveSearchValue}
            showSearch={effectiveShowSearch}
        >
            {children}
        </Select>
    );

    function renderPopup(menu: React.ReactElement) {
        return (
            <>
                <div className={styles.selectActions} onMouseDown={event => event.preventDefault()}>
                    <Button
                        type="link"
                        size="small"
                        disabled={disabled || visibleSelectAllValues.length === 0}
                        onClick={selectVisibleValues}
                    >
                        {selectAllLabel}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        disabled={disabled}
                        onClick={deselectVisibleValues}
                    >
                        {deselectAllLabel}
                    </Button>
                    <span className={styles.selectionSummary}>{renderSelectionSummary()}</span>
                </div>
                {menu}
            </>
        );
    }

    function renderSelectionSummary() {
        if (selectedCount === 0) {
            return "No items selected";
        }

        if (selectedCount === 1) {
            return "1 item selected";
        }

        return `${selectedCount} items selected`;
    }

    function selectVisibleValues() {
        if (!hasActiveSearch) {
            updateValue(selectAllValues);

            return;
        }

        updateValue(Array.from(new Set([...selectedValue, ...visibleSelectAllValues])));
    }

    function deselectVisibleValues() {
        if (!hasActiveSearch) {
            updateValue([]);

            return;
        }

        const visibleValues = new Set(visibleSelectAllValues);

        updateValue(selectedValue.filter(selected => !visibleValues.has(selected)));
    }

    function handleSelectChange(newValue: SelectValue[]) {
        updateValue(newValue);

        if (searchConfig.autoClearSearchValue !== false && searchConfig.searchValue === undefined) {
            setInternalSearchValue("");
        }
    }

    function handleOpenChange(open: boolean) {
        if (!open) {
            setInternalSearchValue("");
        }

        onOpenChange?.(open);
    }

    function handleSearch(newSearchValue: string) {
        setInternalSearchValue(newSearchValue);

        searchConfig.onSearch?.(newSearchValue);
    }

    function updateValue(newValue: SelectValue[]) {
        if (!isControlled) {
            setInternalValue(newValue);
        }

        onChange?.(newValue);
    }
}

function createOptionTreeFromOptions(
    options: DefaultOptionType[],
    fieldNames: SelectProps<SelectValue[]>["fieldNames"]
): SelectOptionNode[] {
    const valueField = fieldNames?.value ?? "value";
    const optionsField = fieldNames?.options ?? "options";

    return options.map(option => {
        const childOptions = option[optionsField];

        if (Array.isArray(childOptions)) {
            return {
                option,
                children: createOptionTreeFromOptions(childOptions, fieldNames)
            };
        }

        const value = option[valueField];

        return {
            option,
            value: typeof value === "string" ? value : undefined
        };
    });
}

function createOptionTreeFromChildren(children: React.ReactNode): SelectOptionNode[] {
    const optionTree: SelectOptionNode[] = [];

    React.Children.forEach(children, child => {
        if (!React.isValidElement<SelectOptionElementProps>(child)) {
            return;
        }

        if (child.type === React.Fragment) {
            optionTree.push(...createOptionTreeFromChildren(child.props.children));

            return;
        }

        const {children: optionChildren, value: optionValue, ...optionProps} = child.props;

        if (isSelectOptionGroup(child)) {
            const groupChildren = createOptionTreeFromChildren(optionChildren);
            const option = {
                key: child.key,
                label: child.key,
                ...optionProps,
                options: groupChildren.map(groupChild => groupChild.option)
            } as DefaultOptionType;

            optionTree.push({option, children: groupChildren});

            return;
        }

        const value = optionValue ?? child.key;
        const option = {
            key: child.key,
            value,
            children: optionChildren,
            ...optionProps
        } as unknown as DefaultOptionType;

        optionTree.push({
            option,
            value: typeof value === "string" ? value : undefined
        });
    });

    return optionTree;
}

function isSelectOptionGroup(option: React.ReactElement) {
    return Boolean((option.type as {isSelectOptGroup?: boolean}).isSelectOptGroup);
}

function getVisibleSelectAllValues(
    selectAllValues: SelectValue[],
    optionTree: SelectOptionNode[],
    searchValue: string,
    filterOption: Props["filterOption"],
    optionFilterProp: Props["optionFilterProp"],
    fieldNames: Props["fieldNames"],
    childrenAsData: boolean
) {
    if (!searchValue || filterOption === false) {
        return selectAllValues;
    }

    const matchingValues = new Set<SelectValue>();
    const configuredFilterProperties = normalizeFilterProperties(optionFilterProp);
    const labelField = fieldNames?.label ?? (childrenAsData ? "children" : "label");
    const filterConfig: OptionFilterConfig = {
        filterOption,
        filterProperties: configuredFilterProperties,
        groupLabelField: labelField === "children" ? "label" : labelField,
        searchValue,
        valueField: fieldNames?.value ?? "value"
    };

    collectMatchingValues(optionTree, filterConfig, matchingValues);

    return selectAllValues.filter(value => matchingValues.has(value));
}

function normalizeFilterProperties(optionFilterProp: Props["optionFilterProp"]) {
    if (!optionFilterProp || optionFilterProp.length === 0) {
        return undefined;
    }

    return Array.isArray(optionFilterProp) ? optionFilterProp : [optionFilterProp];
}

function collectMatchingValues(
    optionTree: SelectOptionNode[],
    filterConfig: OptionFilterConfig,
    matchingValues: Set<SelectValue>
) {
    optionTree.forEach(optionNode => {
        const matchesSearch = optionMatchesSearch(optionNode.option, Boolean(optionNode.children), filterConfig);

        if (optionNode.children) {
            if (matchesSearch) {
                collectAllValues(optionNode.children, matchingValues);
            } else {
                collectMatchingValues(optionNode.children, filterConfig, matchingValues);
            }

            return;
        }

        if (optionNode.value !== undefined && matchesSearch) {
            matchingValues.add(optionNode.value);
        }
    });
}

function collectAllValues(optionTree: SelectOptionNode[], matchingValues: Set<SelectValue>) {
    optionTree.forEach(optionNode => {
        if (optionNode.children) {
            collectAllValues(optionNode.children, matchingValues);

            return;
        }

        if (optionNode.value !== undefined) {
            matchingValues.add(optionNode.value);
        }
    });
}

function optionMatchesSearch(
    option: DefaultOptionType,
    isGroup: boolean,
    filterConfig: OptionFilterConfig
) {
    if (typeof filterConfig.filterOption === "function") {
        return filterConfig.filterOption(filterConfig.searchValue, option);
    }

    const defaultFilterProperty = isGroup ? filterConfig.groupLabelField : filterConfig.valueField;
    const filterProperties = filterConfig.filterProperties ?? [defaultFilterProperty];
    const normalizedSearchValue = filterConfig.searchValue.toUpperCase();

    return filterProperties.some(property => optionValueIncludes(option[property], normalizedSearchValue));
}

function optionValueIncludes(optionValue: unknown, normalizedSearchValue: string) {
    const values = Array.isArray(optionValue) ? optionValue : [optionValue];
    const normalizedOptionValue = values.join("").toUpperCase();

    return normalizedOptionValue.includes(normalizedSearchValue);
}

function normalizeValue(rawValue: SelectProps<SelectValue[]>["value"] | SelectProps<SelectValue[]>["defaultValue"]) {
    return Array.isArray(rawValue) ? rawValue : [];
}

export default SelectAllMultiSelect;
